import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@material-ui/core";
import CloseRoundedIcon from "@material-ui/icons/CloseRounded";
import useCommonStyles from "./style";
import { MODULE_FORMS } from "./assessmentModuleForms";
import {
  getAssessmentQuestionsRequest,
  calculateAssessmentModuleRequest,
} from "../../../PatientApiActions";

// A few modules categorise their items by raw Sanskrit keys; give those a
// readable heading. Anything else is shown as the API returns it.
const CATEGORY_LABELS = {
  ahara: "Āhāra (Diet)",
  vihara: "Vihāra (Lifestyle)",
};

/**
 * Edit a single assessment module's questions and recompute its result. Handles
 * every module family via MODULE_FORMS: single-select MCQ, item checklists, and
 * exam option groups. On Save it calls /calculate (which also persists to the
 * session) and hands the fresh result back.
 */
const AssessmentModulePopup = ({
  open,
  moduleId,
  moduleLabel,
  phone,
  sessionModule,
  onClose,
  onSaved,
}) => {
  const classes = useCommonStyles();
  const dispatch = useDispatch();
  const config = (moduleId && MODULE_FORMS[moduleId]) || null;
  const mode = config ? config.mode : "sections";

  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [questions, setQuestions] = useState(null);
  const [mcqAnswers, setMcqAnswers] = useState({}); // { Qn: index }
  const [singleVals, setSingleVals] = useState({}); // { qKey: index }
  const [multiVals, setMultiVals] = useState({}); // { qKey: [ids] }
  const [followupVal, setFollowupVal] = useState(""); // nidana AH-05 follow-up index
  const [indriyaVals, setIndriyaVals] = useState({}); // { organKey: [labels] }
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !moduleId) return;
    let active = true;
    setStatus("loading");
    dispatch(getAssessmentQuestionsRequest(moduleId))
      .then((data) => {
        if (!active) return;
        setQuestions(data || {});
        const m = sessionModule || {};
        if (mode === "mcq") {
          setMcqAnswers({ ...(m.answers || {}) });
        } else if (mode === "indriya") {
          const keys = (data && data.ORGAN_KEYS) || [];
          const seed = {};
          keys.forEach((k) => {
            seed[k] =
              m.selections && Array.isArray(m.selections[k])
                ? m.selections[k]
                : [];
          });
          setIndriyaVals(seed);
        } else {
          const singles = {};
          const multis = {};
          (config.sections || []).forEach((s) => {
            const opts = (data && data[s.qKey]) || [];
            if (s.type === "single") {
              const id = s.current(m);
              const idx = opts.findIndex((o) => o.id === id);
              singles[s.qKey] = idx >= 0 ? idx : "";
            } else {
              multis[s.qKey] = s.current(m) || [];
            }
          });
          setSingleVals(singles);
          setMultiVals(multis);
          if (config.followup) {
            const fc = m[config.followup.calcField];
            setFollowupVal(typeof fc === "number" ? fc : "");
          }
        }
        setStatus("ready");
      })
      .catch(() => active && setStatus("error"));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, moduleId]);

  const toggleMulti = (qKey, id) =>
    setMultiVals((prev) => {
      const cur = prev[qKey] || [];
      return {
        ...prev,
        [qKey]: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id],
      };
    });

  const toggleIndriya = (organ, label) =>
    setIndriyaVals((prev) => {
      const cur = prev[organ] || [];
      return {
        ...prev,
        [organ]: cur.includes(label)
          ? cur.filter((x) => x !== label)
          : [...cur, label],
      };
    });

  const buildPayload = () => {
    if (mode === "mcq") return { phone, answers: mcqAnswers };
    if (mode === "indriya") {
      const keys = (questions && questions.ORGAN_KEYS) || [];
      const selections = {};
      keys.forEach((k) => {
        selections[k] = indriyaVals[k] || [];
      });
      return { phone, selections };
    }
    const payload = { phone, ...(config.extra ? config.extra(sessionModule || {}) : {}) };
    (config.sections || []).forEach((s) => {
      if (s.type === "single") {
        const v = singleVals[s.qKey];
        payload[s.calcField] = v === "" || v == null ? null : Number(v);
      } else {
        payload[s.calcField] = multiVals[s.qKey] || [];
      }
    });
    // Follow-up (nidana): always present — an index only when its trigger item
    // is ticked, null otherwise.
    if (config.followup) {
      const triggered = (multiVals[config.followup.itemsKey] || []).includes(
        config.followup.triggerId
      );
      payload[config.followup.calcField] =
        triggered && followupVal !== "" && followupVal != null
          ? Number(followupVal)
          : null;
    }
    return payload;
  };

  const handleSave = () => {
    if (saving || !phone) return;
    setSaving(true);
    dispatch(calculateAssessmentModuleRequest(moduleId, buildPayload()))
      .then((result) => {
        setSaving(false);
        if (onSaved) onSaved(moduleId, result);
        if (onClose) onClose();
      })
      .catch(() => setSaving(false));
  };

  // A checklist item's short label is only a handle; the module's real question
  // (falling back to its long description) is what the doctor needs to read.
  const renderOptionLabel = (opt) => {
    const detail = opt.question || opt.desc || opt.description;
    return (
      <span>
        <span className={classes.ampOptionLabel}>{opt.label}</span>
        {detail ? (
          <span className={classes.ampOptionQuestion}>{detail}</span>
        ) : null}
      </span>
    );
  };

  // Checklist items carry a `category` (e.g. "Adaptability", "Stress
  // Handling"); keep the API order and only show headings when a section
  // actually spans more than one category.
  const groupByCategory = (opts) => {
    const order = [];
    const byCategory = {};
    opts.forEach((opt) => {
      const cat = opt.category || "";
      if (!byCategory[cat]) {
        byCategory[cat] = [];
        order.push(cat);
      }
      byCategory[cat].push(opt);
    });
    if (order.length < 2) return [{ category: "", items: opts }];
    return order.map((cat) => ({ category: cat, items: byCategory[cat] }));
  };

  const renderChecklist = (qKey, opts) => (
    <Box className={classes.ampCheckList}>
      {groupByCategory(opts).map((group) => (
        <React.Fragment key={group.category || "all"}>
          {group.category ? (
            <Typography className={classes.ampCategoryTitle}>
              {CATEGORY_LABELS[group.category] || group.category}
            </Typography>
          ) : null}
          {group.items.map((opt, idx) => (
            <FormControlLabel
              key={opt.id || opt.label || idx}
              className={classes.ampCheckItem}
              control={
                <Checkbox
                  size="small"
                  color="primary"
                  checked={(multiVals[qKey] || []).includes(opt.id)}
                  onChange={() => toggleMulti(qKey, opt.id)}
                />
              }
              label={renderOptionLabel(opt)}
            />
          ))}
        </React.Fragment>
      ))}
    </Box>
  );

  const renderMcq = () => {
    const qs = (questions && (questions.QUESTIONS || questions.questions)) || [];
    return qs.map((q) => {
      const opts = Array.isArray(q.options) ? q.options : [];
      const sel = mcqAnswers[q.id];
      return (
        <Box key={q.id} mb={2.5}>
          <Typography className={classes.ampQuestion}>
            {q.question || q.text || q.label}
          </Typography>
          <FormControl variant="outlined" size="small" fullWidth>
            <Select
              value={sel == null ? "" : sel}
              displayEmpty
              onChange={(e) =>
                setMcqAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))
              }
            >
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>
              {opts.map((opt, idx) => (
                <MenuItem key={idx} value={idx}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      );
    });
  };

  const renderIndriya = () => {
    const keys = (questions && questions.ORGAN_KEYS) || [];
    const names = (questions && questions.ORGANS) || [];
    const symptoms = (questions && questions.ORGAN_SYMPTOMS) || {};
    return keys.map((k, i) => {
      const opts = symptoms[k] || [];
      return (
        <Box key={k} mb={2.5}>
          <Typography className={classes.ampQuestion}>
            {names[i] || k}
          </Typography>
          <Box>
            {opts.map((opt, idx) => (
              <FormControlLabel
                key={opt.label || idx}
                control={
                  <Checkbox
                    size="small"
                    color="primary"
                    checked={(indriyaVals[k] || []).includes(opt.label)}
                    onChange={() => toggleIndriya(k, opt.label)}
                  />
                }
                label={
                  <span className={classes.ampOptionLabel}>{opt.label}</span>
                }
              />
            ))}
          </Box>
        </Box>
      );
    });
  };

  // nidana: the follow-up single-select only shows once its trigger item (AH-05)
  // is ticked; its options live inside that item's `followup.options`.
  const renderFollowup = () => {
    if (!config || !config.followup) return null;
    const items = (questions && questions[config.followup.itemsKey]) || [];
    const triggered = (multiVals[config.followup.itemsKey] || []).includes(
      config.followup.triggerId
    );
    if (!triggered) return null;
    const item = items.find((it) => it.id === config.followup.triggerId);
    const opts = (item && item.followup && item.followup.options) || [];
    if (!opts.length) return null;
    return (
      <Box mb={2.5}>
        <Typography className={classes.ampQuestion}>
          {config.followup.title}
        </Typography>
        <FormControl variant="outlined" size="small" fullWidth>
          <Select
            value={followupVal === "" ? "" : followupVal}
            displayEmpty
            onChange={(e) => setFollowupVal(e.target.value)}
          >
            <MenuItem value="">
              <em>Select</em>
            </MenuItem>
            {opts.map((opt, idx) => (
              <MenuItem key={idx} value={idx}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  };

  const renderSections = () =>
    ((config && config.sections) || []).map((s) => {
      const opts = (questions && questions[s.qKey]) || [];
      if (!opts.length) return null;
      return (
        <Box key={s.qKey} mb={2.5}>
          <Typography className={classes.ampQuestion}>{s.title}</Typography>
          {s.type === "single" ? (
            <FormControl variant="outlined" size="small" fullWidth>
              <Select
                value={singleVals[s.qKey] === "" ? "" : singleVals[s.qKey]}
                displayEmpty
                onChange={(e) =>
                  setSingleVals((prev) => ({ ...prev, [s.qKey]: e.target.value }))
                }
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                {opts.map((opt, idx) => (
                  <MenuItem key={opt.id || idx} value={idx}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            renderChecklist(s.qKey, opts)
          )}
        </Box>
      );
    });

  // A stored module that is nothing but a classification has no answers behind
  // it (the result was saved on its own), so the empty form is the data, not a
  // bug — say so. An engine-computed result always carries its inputs and
  // scores alongside, so a genuine all-clear module never trips this.
  const answersMissing =
    !!sessionModule &&
    !!sessionModule.classification &&
    Object.keys(sessionModule).filter((k) => k !== "classification").length ===
      0;
  const showEmptyNote = status === "ready" && answersMissing;

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="md" fullWidth>
      <Box className={classes.reportDialogTitle}>
        <Typography className={classes.reportDialogTitleText}>
          {moduleLabel || "Assessment"}
        </Typography>
        <IconButton aria-label="close" onClick={onClose} size="small">
          <CloseRoundedIcon />
        </IconButton>
      </Box>
      <DialogContent dividers>
        {status === "loading" && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={26} />
          </Box>
        )}
        {status === "error" && (
          <Typography className={classes.hiPlaceholderNote}>
            Could not load this module's questions.
          </Typography>
        )}
        {showEmptyNote && (
          <Typography className={classes.ampEmptyNote}>
            No saved answers behind this module's result — only the result
            itself was stored. Answer the questions below and Save to recompute
            it from the findings.
          </Typography>
        )}
        {status === "ready" &&
          config &&
          (mode === "mcq" ? (
            renderMcq()
          ) : mode === "indriya" ? (
            renderIndriya()
          ) : (
            <>
              {renderSections()}
              {renderFollowup()}
            </>
          ))}
      </DialogContent>
      <Box display="flex" justifyContent="flex-end" p={2} style={{ gap: 12 }}>
        <Button onClick={onClose} className={classes.hlFullBtn} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disableElevation
          disabled={saving || status !== "ready"}
          className={classes.hiSaveBtn}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </Box>
    </Dialog>
  );
};

export default AssessmentModulePopup;
